import React, { useCallback, useContext, useEffect, useState } from "react";
import ContentLayout from "../../component/ContentLayout";
import CustomButton from "../../component/Button";
import axios from "../../util/axios";
import { Context } from "../../component/Context/ErrorContext";
import { useParams } from "react-router-dom";
import moment from "moment/moment";
import { IoMdArrowRoundBack } from "react-icons/io";
import { Grid } from "@mui/material";
import CustomTextField from "../../component/TextField";
import CustomModal from "../../component/Modal";
import * as Yup from "yup";
import { useFormik } from "formik";
import AgGridTable from "../../component/AgGridTable";
import TextEditor from "../../component/TextEditor";
import PhoneInput from "../../component/PhoneInput";
import { isOnlyCountryCode } from "../../util/util";

const color: any = {
  active: "border-green text-green",
  pending: "border-pending text-pending",
  invited: "border-invited text-invited",
  activeBg: "bg-green text-green",
  pendingBg: "bg-pending text-pending",
  invitedBg: "bg-invited text-invited",
};

const tabs = [
  { id: 1, name: "SMS", key: "text" },
  { id: 2, name: "Email", key: "email" },
];

const defaultEmailBody = `<p style="font-size: 16px; line-height: 1.5; margin-bottom: 15px;">
      You have been added to <strong></strong> by your administrator. To complete your registration and get started, please click the link below:
    </p>`;

const EmployeeDetails = () => {
  const { setError } = useContext(Context);
  const params = useParams();
  const [loading, setLoading] = useState(false);
  const [editEmployeeModal, setEditEmployeeModal] = useState(false);
  const [colDefs, setCol] = useState<any>([]);
  const [inviteDriver, setInviteDriver] = useState<any>([]);
  const [isEmployeeEdit, setIsEmployeeEdit] = useState(false);
  const [employee, setEmployee] = useState<any>([]);
  const [isInvite, setIsInvite] = useState(false);
  const [activeTab, setActiveTab] = useState("text");

  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [total, setTotal] = useState(0);

  const [country, setCountry] = useState<any>({});

  const defaultInviteMessage = {
    user_id: "",
    channel: "text",
    body: "You have been added to  by your administrator. To complete your registration and get started, please click the link below:",
    subject: "Top 5 Traffic Tips",
  };
  const [inviteMessage, setInviteMessage] = useState(defaultInviteMessage);
  const [emailBody, setEmailBody] = useState(defaultEmailBody);

  const sendInvite = async (e: any) => {
    e.preventDefault();
    setLoading(true);
    await axios({
      URL: `/omw/subadmin-inviteUser`,
      method: "POST",
      data: {
        ...inviteMessage,
        body: activeTab === "email" ? emailBody : inviteMessage.body,
      },
    })
      .then(() => {
        setInviteMessage(defaultInviteMessage as any);
        setEmailBody("");
        setEditEmployeeModal(false);
        setActiveTab("text");
        resetForm();
      })
      .catch((error) => setError(error?.response?.data?.message))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (inviteDriver.length) {
      setCol([
        {
          headerName: "STATUS",
          field: "status" as keyof {
            make: string;
            model: string;
            price: number;
            status: string;
          },
          cellRenderer: (params: { value: string }) => {
            return (
              <div className={"flex justify-center items-center"}>
                <div
                  className={`flex justify-center items-center border border-solid ${
                    color[params.value.toLowerCase() as string]
                  } rounded-xl px-4 py-1 !h-[25px] text-sm font-semibold gap-1`}
                >
                  <div
                    className={`w-[6px] h-[6px] ${
                      color[(params.value.toLowerCase() as string) + "Bg"]
                    } rounded-full`}
                  />
                  {params.value[0].toUpperCase() +
                    params.value.slice(1, params.value.length)}
                </div>
              </div>
            );
          },
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        {
          headerName: "ALERT TYPE",
          field: "alert_type",
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        {
          headerName: "SENT DATE",
          field: "created_at",
          cellRenderer: (params: { value: string }) => {
            return <p>{moment(params.value).format("MM/DD/YYYY")}</p>;
          },
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        {
          headerName: "COMPANY",
          field: "company",
          cellStyle: {
            display: "flex",
            alignItems: "center",
          },
        },
        { headerName: "EMAIL SUBJECT", field: "subject" },
      ]);
    }
  }, [inviteDriver]);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  useEffect(() => {
    fetchInviteDriver(page, pageSize);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize]);

  const fetchEmployeeData = async (id = "") => {
    setLoading(true);
    axios({
      URL: `/omw/subAdmin/User/${params.id}`,
      method: "GET",
    })
      .then((res: any) => {
        if (res.data) {
          setEmployee(res?.data?.data);
        }
      })
      .catch((err) => setError(err.response?.data?.message))
      .finally(() => setLoading(false));
  };

  const fetchInviteDriver = async (page = 1, pageSize = 10) => {
    setLoading(true);
    axios({
      URL: `/omw/subadmin-invitedUserList?employee_id=${params.id}&page=${page}&limit=${pageSize}`,
      method: "GET",
    })
      .then((res: any) => {
        if (res.data) {
          setInviteDriver(res?.data?.data?.users);
          setTotal(res?.data?.data?.total || 0);
        }
      })
      .catch((err) => setError(err.response?.data?.message))
      .finally(() => setLoading(false));
  };

  const validationSchema = Yup.object().shape({
    name: Yup.string().required("Name is required"),
    email: Yup.string().required("Email is required"),
    phone: Yup.string().required("phone is required"),
  });

  const formik = useFormik({
    initialValues: {
      id: 0,
      name: "",
      email: "",
      phone: "",
      ip_address: "",
      location: "",
      status: "",
      created_dt: "",
      updated_dt: "",
    },
    validationSchema: validationSchema,
    onSubmit: (values, { setFieldError }) => {
      try {
        if (values.phone === "" || isOnlyCountryCode(values.phone, country)) {
          setFieldError("phone", "Required");
        } else {
          axios(
            isEmployeeEdit
              ? {
                  URL: `/omw/subAdmin/User/${values?.id}`,
                  method: "PATCH",
                  data: values,
                }
              : {
                  URL: "/omw/subAdmin/createUser",
                  method: "POST",
                  data: values,
                },
          )
            .then(() => {
              resetForm();
              setEditEmployeeModal(false);
              fetchEmployeeData();
            })
            .catch((err) => setError(err.response?.data?.message));
        }
      } catch (error) {}
    },
  });

  const {
    handleSubmit,
    values,
    setValues,
    handleChange,
    setFieldValue,
    resetForm,
    errors,
  } = formik;

  const gridCard = (label = "", value = "-") => {
    return (
      <div className={"flex flex-col justify-center items-start gap-2 p-4"}>
        <div className={"text-primaryText font-bold text-xl"}>{label} :</div>
        <div className={"text-primaryText text-md"}>{value}</div>
      </div>
    );
  };

  const handleInviteChange = (e: any) => {
    setInviteMessage((prev) => ({
      ...prev,
      [e.target?.name]: e.target?.value,
    }));
  };

  const handleEditorChange = useCallback((value: string) => {
    setEmailBody(value);
  }, []);

  return (
    <ContentLayout
      className={"items-start rounded-3xl m-2 gap-6 overflow-y-scroll"}
    >
      <div className={"flex justify-between items-center w-full"}>
        <p
          className={
            "text-3xl font-bold text-primaryText flex items-center gap-2"
          }
        >
          <IoMdArrowRoundBack
            onClick={() => window.history.back()}
            className={"cursor-pointer"}
          />
          Employee Details
        </p>
      </div>
      <div className={"w-full bg-primary rounded-3xl p-4"}>
        <div className={"gap-4 grid grid-cols-4"}>
          {gridCard("Name", employee?.name)}
          {gridCard("Email", employee?.email)}
          {gridCard("Phone", employee?.phone)}
          {gridCard("IP Address", employee?.ip_address)}
          {gridCard("Location", employee?.location)}
          {gridCard("Status", employee?.status)}
          {gridCard(
            "Create Date",
            moment(employee?.created_dt).format("MM-DD-YYYY"),
          )}
          {gridCard(
            "Update Date",
            moment(employee?.updated_dt).format("MM-DD-YYYY"),
          )}
        </div>
        <div className={"w-full p-4 flex justify-start items-center gap-4"}>
          <CustomButton
            className={"w-full"}
            title={"Edit"}
            onClick={() => {
              setValues(employee);
              setEditEmployeeModal(true);
              setIsEmployeeEdit(true);
            }}
          />
          <CustomButton
            className={"w-full"}
            title={"Invite"}
            onClick={() => {
              setInviteMessage((prev) => ({
                ...prev,
                user_id: employee?.id,
              }));
              setEditEmployeeModal(true);
              setIsEmployeeEdit(false);
              setIsInvite(true);
              setValues(employee);
            }}
          />
        </div>
      </div>
      <div className="ag-theme-quartz w-full h-1/2">
        <AgGridTable
          rowData={inviteDriver}
          colDefs={colDefs}
          loading={loading}
          height={"100%"}
          pagination={true}
          pageSize={pageSize}
          page={page}
          pageChange={setPage}
          pageSizeChange={setPageSize}
          total={total}
        />
      </div>
      <CustomModal
        open={editEmployeeModal}
        onClose={() => {
          setIsInvite(false);
          setIsEmployeeEdit(false);
          setEditEmployeeModal(false);
          setInviteMessage(defaultInviteMessage);
          setActiveTab("text");
          setEmailBody("");
          resetForm();
        }}
        label={isInvite ? "Invite" : "Update Employee"}
      >
        {isInvite ? (
          <div className={"flex flex-col gap-3"}>
            <div className={"flex flex-col gap-1"}>
              <p className={"text-primaryText text-sm"}>Invite Type</p>
              <div className={"flex w-full gap-1 bg-gray p-1 rounded-xl"}>
                {tabs.map((tab, index) => (
                  <div
                    key={tab.key}
                    className={`flex-1 flex justify-center items-center py-3 text-white font-medium rounded-xl cursor-pointer ${
                      activeTab === tab.key
                        ? "bg-primary"
                        : "bg-gray opacity-60"
                    }`}
                    onClick={() => {
                      setActiveTab(tab.key);
                      setInviteMessage((prev) => ({
                        ...prev,
                        channel: tab.key,
                      }));
                      if (tab.key === "email") {
                        setEmailBody(defaultEmailBody);
                      }
                    }}
                  >
                    {tab.name}
                  </div>
                ))}
              </div>
            </div>
            <form onSubmit={sendInvite} className={"flex flex-col gap-3"}>
              <div className={"flex flex-col gap-2 w-full"}>
                <CustomTextField
                  label={"Subject"}
                  type={"text"}
                  name={"subject"}
                  onChange={handleInviteChange}
                  value={inviteMessage?.subject}
                  disabled={loading}
                  required={activeTab === "email"}
                />
              </div>
              <div className={"flex flex-col gap-2 w-full"}>
                <CustomTextField
                  label={"Body"}
                  type={"textarea"}
                  name={"body"}
                  onChange={handleInviteChange}
                  value={inviteMessage?.body}
                  multiline={true}
                  minRows={3}
                  className={`h-auto ${
                    activeTab === "email" ? "hidden" : "flex"
                  }`}
                  disabled={loading}
                  required={true}
                />
                <TextEditor
                  className={`${
                    activeTab === "email" ? "flex flex-col h-[150px]" : "hidden"
                  }`}
                  onChange={handleEditorChange}
                  value={emailBody}
                />
              </div>
              <CustomButton
                title={"Send"}
                type={"submit"}
                className={`!py-3 disabled:opacity-80 opacity-100 disabled:cursor-not-allowed`}
                disabled={loading}
              />
            </form>
          </div>
        ) : (
          <form
            onSubmit={handleSubmit}
            className={"flex flex-col mt-5 w-full max-w-full gap-4"}
            autoComplete={"off"}
          >
            <Grid
              container
              spacing={2}
              className={"overflow-auto max-h-[600px]"}
            >
              <Grid item xs={12} sm={8} md={12}>
                <CustomTextField
                  label={"Name"}
                  type={"text"}
                  name={"name"}
                  onChange={handleChange}
                  value={values?.name}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={8} md={12}>
                <CustomTextField
                  label={"Email"}
                  type={"text"}
                  name={"email"}
                  onChange={handleChange}
                  value={values?.email}
                  required
                />
              </Grid>
              <Grid item xs={12} sm={6} md={12}>
                <PhoneInput
                  label={"Phone"}
                  onChange={(v) => setFieldValue("phone", v)}
                  value={values?.phone}
                  countryChange={setCountry}
                  required
                  errorMsg={errors?.phone || ""}
                />
              </Grid>
            </Grid>
            <CustomButton
              title={isEmployeeEdit ? "Update Employee" : "Invite"}
              type={"submit"}
              className={"!py-3"}
            />
          </form>
        )}
      </CustomModal>
    </ContentLayout>
  );
};

export default EmployeeDetails;
